import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Dialog, DialogContent, Fade, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import i18next from "../../i18n/i18n";
import { fetchUserContentFile } from "../../api";
import { Attachment, getFileProperties, ServerSideFile } from "./FileAttachments";
import {
    EXCEL_TYPES,
    getFileIconByMimeType,
    getFileType,
    getReadableFileSize,
    IMAGE_TYPES,
    PDF_TYPES,
    POWERPOINT_TYPES,
    TEXT_TYPES,
    WORD_TYPES
} from "./fileUtils";

export { IMAGE_TYPES, TEXT_TYPES };

interface FilePreviewDialogProps {
    files: Attachment[];
    initialIndex?: number;
    open: boolean;
    onClose: () => void;
    currentPageToView?: number;
}

function PDFViewer({ file, currentPageToView }: { file: File | ServerSideFile; currentPageToView?: number }) {
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [error, setError] = useState(false);
    const urlRef = useRef<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let newUrl: string | null = null;

        // Reset state for each new file so stale error/URL never bleeds across files
        setError(false);
        setPdfUrl("");

        // Revoke the previous URL before creating a new one
        if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        }

        try {
            if (file instanceof File) {
                newUrl = URL.createObjectURL(file);
            } else if (file && typeof file === "object" && file["content_path"]) {
                // Fetch via authenticated API so auth redirects (login/home page) never
                // end up rendered inside the iframe.
                fetchUserContentFile(file["content_path"] as string)
                    .then((res: Response) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.blob();
                    })
                    .then((blob: Blob) => {
                        const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                        if (isMounted) {
                            urlRef.current = blobUrl;
                            setPdfUrl(blobUrl);
                        } else {
                            URL.revokeObjectURL(blobUrl);
                        }
                    })
                    .catch(() => {
                        if (isMounted) setError(true);
                    });
                // Return cleanup so isMounted is flipped on file change/unmount,
                // preventing state updates and blob URL leaks after cleanup.
                return () => {
                    isMounted = false;
                };
            } else if (file && typeof file === "object" && file["content"] && file["type"] === "application/pdf") {
                // Legacy: Handle base64-encoded PDF content as binary
                const content = file["content"];
                if (typeof content === "string") {
                    try {
                        const bytes = decodeBase64ToUint8Array(content);
                        const blob = new Blob([bytes.slice().buffer], { type: "application/pdf" });
                        newUrl = URL.createObjectURL(blob);
                    } catch {
                        if (isMounted) {
                            setError(true);
                        }
                        return;
                    }
                }
            }

            if (newUrl) {
                urlRef.current = newUrl;
                // Use setTimeout to ensure the blob URL is ready before setting it
                setTimeout(() => {
                    if (isMounted) {
                        setPdfUrl(newUrl!);
                    }
                }, 0);
            } else {
                if (isMounted) {
                    setError(true);
                }
            }
        } catch (err) {
            console.error("Error creating PDF URL:", err);
            if (isMounted) {
                setError(true);
            }
        }

        // Cleanup: only revoke URL on unmount
        return () => {
            isMounted = false;
            // Don't revoke here - let the next render or unmount handle it
        };
    }, [file]);

    // Cleanup all URLs on component unmount
    useEffect(() => {
        return () => {
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = null;
            }
        };
    }, []);

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    flexDirection: "column",
                    gap: 2
                }}
            >
                <PictureAsPdfIcon sx={{ fontSize: 64, color: "rgba(255, 255, 255, 0.5)" }} />
                <Typography color="white">{i18next.t("filePreview.pdfLoadError")}</Typography>
            </Box>
        );
    }

    if (!pdfUrl) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%"
                }}
            >
                <Typography color="white">{i18next.t("filePreview.pdfLoading")}</Typography>
            </Box>
        );
    }

    return (
        <iframe
            src={pdfUrl + "#page=" + (currentPageToView && currentPageToView > 0 ? currentPageToView : 1)}
            title={file.name}
            style={{
                width: "100%",
                height: "100%",
                border: "none"
            }}
        />
    );
}

// Helper to decode base64 with Shift_JIS and ISO-2022-JP fallback for text files
const decodeBase64WithShiftJISFallback = (content: string): string => {
    let bytes: Uint8Array;
    try {
        bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    } catch {
        return content;
    }
    return decodeArrayBufferWithFallback(bytes, content);
};

// Helper to decode ArrayBuffer/Uint8Array with encoding fallback for text files
const decodeArrayBufferWithFallback = (buffer: ArrayBuffer | Uint8Array, content?: string): string => {
    let bytes: Uint8Array = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const tryDecode = (encoding: string) => {
        try {
            return new TextDecoder(encoding, { fatal: true }).decode(bytes);
        } catch {
            return null;
        }
    };
    // Try UTF-8 first
    let decoded = tryDecode("utf-8");
    if (decoded && decoded.includes("\uFFFD")) {
        decoded = null;
    }
    return (
        decoded ??
        // @ts-ignore: Shift_JIS may not be supported in all browsers
        tryDecode("shift_jis") ??
        // @ts-ignore: ISO-2022-JP may not be supported in all browsers
        tryDecode("iso-2022-jp") ??
        content ??
        ""
    );
};

// Helper to decode base64 to Uint8Array for binary files
const decodeBase64ToUint8Array = (content: string): Uint8Array => {
    try {
        const binary = atob(content);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes;
    } catch {
        return new Uint8Array();
    }
};

function TextFileRenderer({ file }: { file: File | ServerSideFile }) {
    const [content, setContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (file instanceof File) {
            // Try to read as ArrayBuffer and apply fallback decoding
            const reader = new FileReader();
            reader.onload = e => {
                try {
                    const result = e.target?.result;
                    if (typeof result === "string") {
                        setContent(result);
                    } else {
                        const buffer = result as ArrayBuffer;
                        const decoded = decodeArrayBufferWithFallback(buffer);
                        setContent(decoded);
                    }
                } catch {
                    // fallback to read as text
                    const reader2 = new FileReader();
                    reader2.onload = ev => setContent(ev.target?.result as string);
                    reader2.onerror = () => setError(i18next.t("filePreview.textErrorMessage"));
                    reader2.readAsText(file);
                }
                setLoading(false);
            };
            reader.onerror = () => {
                setError(i18next.t("filePreview.textErrorMessage"));
                setLoading(false);
            };
            if (typeof (reader as any).readAsArrayBuffer === "function") {
                reader.readAsArrayBuffer(file);
            } else if (typeof reader.readAsText === "function") {
                reader.readAsText(file);
            } else {
                setError(i18next.t("filePreview.textErrorMessage"));
                setLoading(false);
            }
        } else if (file["content_path"]) {
            fetchUserContentFile((file as ServerSideFile)["content_path"] as string)
                .then((r: Response) => {
                    if (!r.ok) {
                        throw new Error(`${r.status} ${r.statusText}`);
                    }
                    return r.arrayBuffer();
                })
                .then((buffer: ArrayBuffer) => {
                    setContent(decodeArrayBufferWithFallback(new Uint8Array(buffer)));
                })
                .catch(() => setError(i18next.t("filePreview.textErrorMessage")))
                .finally(() => setLoading(false));
        } else {
            // Legacy: base64-encoded content
            const content = file["content"];
            if (typeof content === "string") {
                setContent(decodeBase64WithShiftJISFallback(content));
            } else {
                setContent(String(content));
            }
            setLoading(false);
        }
    }, [file]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    p: 4
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    {i18next.t("filePreview.textLoading")}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    p: 4
                }}
            >
                <TextSnippetIcon sx={{ fontSize: 96, color: "#1976d2", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" textAlign="center">
                    {i18next.t("filePreview.textErrorTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                p: 2,
                overflow: "auto"
            }}
        >
            <Paper
                sx={{
                    p: 3,
                    bgcolor: "background.paper",
                    minHeight: "100%",
                    fontFamily: "monospace"
                }}
            >
                <Typography
                    component="pre"
                    sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: "0.875rem",
                        lineHeight: 1.5,
                        margin: 0
                    }}
                >
                    {content}
                </Typography>
            </Paper>
        </Box>
    );
}

export function FilePreviewDialog({ files, initialIndex = 0, open, onClose, currentPageToView }: FilePreviewDialogProps) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoom, setZoom] = useState(1);
    const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
    const [imageLoading, setImageLoading] = useState(false);
    const imageBlobUrlRef = useRef<string | null>(null);

    const currentFile = files[currentIndex];
    const hasMultipleFiles = files.length > 1;

    const currentFileType = currentFile ? getFileType(currentFile.file as File) : "";
    const currentFileProps = currentFile ? getFileProperties(currentFile) : undefined;

    // Reset state when dialog opens or file changes
    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setZoom(1);
        }
    }, [open, initialIndex]);

    const handleNext = useCallback(() => {
        if (hasMultipleFiles && currentIndex < files.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setZoom(1);
        }
    }, [hasMultipleFiles, files.length, currentIndex]);

    const handlePrevious = useCallback(() => {
        if (hasMultipleFiles && currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setZoom(1);
        }
    }, [hasMultipleFiles, currentIndex]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!open) return;

            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    if (currentIndex > 0) {
                        handlePrevious();
                    }
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    if (currentIndex < files.length - 1) {
                        handleNext();
                    }
                    break;
                case "Escape":
                    event.preventDefault();
                    onClose();
                    break;
                case "+":
                case "=":
                    event.preventDefault();
                    setZoom(prev => Math.min(prev + 0.25, 3));
                    break;
                case "-":
                    event.preventDefault();
                    setZoom(prev => Math.max(prev - 0.25, 0.25));
                    break;
            }
        },
        [open, handleNext, handlePrevious, onClose, currentIndex, files.length]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        setImageLoading(false);
        if (!currentFile) {
            setImageSrc(undefined);
            return;
        }

        const { type: fileType } = getFileProperties(currentFile);
        if (!IMAGE_TYPES.includes(fileType)) {
            setImageSrc(undefined);
            return;
        }

        if (currentFile.preview) {
            setImageSrc(currentFile.preview);
            return;
        }

        if (currentFile.file instanceof File) {
            const objectUrl = URL.createObjectURL(currentFile.file);
            setImageSrc(objectUrl);
            return () => {
                URL.revokeObjectURL(objectUrl);
            };
        }

        const serverFile = currentFile.file as ServerSideFile;
        if (serverFile.content_path) {
            // Revoke previous blob URL before starting a new fetch
            if (imageBlobUrlRef.current) {
                URL.revokeObjectURL(imageBlobUrlRef.current);
                imageBlobUrlRef.current = null;
            }
            let cancelled = false;
            setImageLoading(true);
            fetchUserContentFile(serverFile.content_path)
                .then((res: Response) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.blob();
                })
                .then((blob: Blob) => {
                    const blobUrl = URL.createObjectURL(blob);
                    if (cancelled) {
                        // Stale response — discard immediately to avoid leak
                        URL.revokeObjectURL(blobUrl);
                        return;
                    }
                    imageBlobUrlRef.current = blobUrl;
                    setImageSrc(blobUrl);
                })
                .catch(() => {
                    if (!cancelled) setImageSrc(undefined);
                })
                .finally(() => {
                    if (!cancelled) setImageLoading(false);
                });
            return () => {
                cancelled = true;
                if (imageBlobUrlRef.current) {
                    URL.revokeObjectURL(imageBlobUrlRef.current);
                    imageBlobUrlRef.current = null;
                }
            };
        }
        if (typeof serverFile.content === "string" && serverFile.content.length > 0) {
            setImageSrc(`data:${fileType};base64,${serverFile.content}`);
            return;
        }

        setImageSrc(undefined);
    }, [currentFile]);

    // Helper to trigger download and cleanup
    const triggerDownload = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownload = () => {
        if (!currentFile) return;
        const fileTypeKey = getFileType(currentFile.file as File);
        const { type: fileType, name: filename } = getFileProperties(currentFile);

        if (currentFile.preview && currentFile.preview.startsWith("blob:")) {
            // Only use blob: URLs directly — they are same-origin and the download attribute is honored
            const link = document.createElement("a");
            link.href = currentFile.preview;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        if (currentFile.file instanceof File) {
            const fileToDownload = currentFile.file;
            if (TEXT_TYPES.includes(fileTypeKey)) {
                // Read as ArrayBuffer and apply fallback decoding, then download as text
                const reader = new FileReader();
                reader.onload = e => {
                    try {
                        const result = e.target?.result;
                        const decoded = typeof result === "string" ? result : decodeArrayBufferWithFallback(result as ArrayBuffer);
                        const blob = new Blob([decoded], { type: fileType || "text/plain" });
                        triggerDownload(blob, filename);
                    } catch {
                        // fallback to download as-is
                        triggerDownload(fileToDownload, filename);
                    }
                };
                reader.onerror = () => {
                    triggerDownload(fileToDownload, filename);
                };
                if (typeof (reader as any).readAsArrayBuffer === "function") {
                    reader.readAsArrayBuffer(fileToDownload);
                } else if (typeof reader.readAsText === "function") {
                    reader.readAsText(fileToDownload);
                } else {
                    triggerDownload(fileToDownload, filename);
                }
            } else {
                // Binary file, download as-is
                triggerDownload(fileToDownload, filename);
            }
            return;
        }

        // Handle content_path (lazy load) for all file types
        const contentPath = currentFile.file["content_path"];
        if (typeof contentPath === "string") {
            // Use a relative URL so the request is always same-origin (cookie auth works,
            // Vite dev proxy forwards /content/ to the backend, download attribute is honored)
            const relativeUrl = `/content/user-files/${contentPath}`;
            fetch(relativeUrl, { credentials: "include" })
                .then(r => {
                    if (!r.ok) {
                        throw new Error(`Download failed: ${r.status} ${r.statusText}`);
                    }
                    return r.blob();
                })
                .then((blob: Blob) => {
                    // Use the server-provided MIME type directly; no need to re-wrap
                    triggerDownload(blob, filename);
                })
                .catch(() => {
                    const link = document.createElement("a");
                    link.href = relativeUrl;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                });
            return;
        }

        // Legacy: Handle base64 content for all file types
        const content = currentFile.file["content"];
        if (typeof content === "string") {
            if (TEXT_TYPES.includes(fileTypeKey)) {
                try {
                    const decodedContent = decodeBase64WithShiftJISFallback(content);
                    const blob = new Blob([decodedContent], { type: fileType || "text/plain" });
                    triggerDownload(blob, filename);
                } catch {
                    const blob = new Blob([content], { type: fileType || "text/plain" });
                    triggerDownload(blob, filename);
                }
            } else {
                try {
                    const bytes = decodeBase64ToUint8Array(content);
                    const blob = new Blob([bytes.slice().buffer], { type: fileType || "application/octet-stream" });
                    triggerDownload(blob, filename);
                } catch {
                    const blob = new Blob([content], { type: fileType || "application/octet-stream" });
                    triggerDownload(blob, filename);
                }
            }
        } else {
            const blob = new Blob([String(content)], { type: fileType || "application/octet-stream" });
            triggerDownload(blob, filename);
        }
    };

    const renderFileContent = () => {
        if (!currentFile) return null;

        const fileType = getFileType(currentFile.file as File);

        if (IMAGE_TYPES.includes(fileType)) {
            if (imageLoading) {
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            p: 4
                        }}
                    >
                        <Typography variant="h6" color="white" textAlign="center">
                            {i18next.t("filePreview.imageLoading")}
                        </Typography>
                    </Box>
                );
            }
            if (!imageSrc) {
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            p: 4
                        }}
                    >
                        <Box sx={{ mb: 2 }}>{getFileIconByMimeType(fileType, 80)}</Box>
                        <Typography variant="h6" color="white" textAlign="center">
                            {i18next.t("filePreview.previewNotAvailable")}
                        </Typography>
                    </Box>
                );
            }
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        overflow: "auto",
                        p: 2
                    }}
                >
                    <img
                        src={imageSrc}
                        alt={currentFileProps?.name ?? currentFile.file.name}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            transform: `scale(${zoom})`,
                            transition: "transform 0.2s ease-in-out",
                            objectFit: "contain"
                        }}
                    />
                </Box>
            );
        }

        if (TEXT_TYPES.includes(fileType)) {
            return <TextFileRenderer file={currentFile.file} />;
        }

        if (PDF_TYPES.includes(fileType)) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        bgcolor: "#525659"
                    }}
                >
                    <PDFViewer file={currentFile.file} currentPageToView={currentPageToView} />
                </Box>
            );
        }

        // Default fallback for other file types (Word, Excel, PowerPoint)
        // Provide helpful information about which apps can open these files
        const getFileTypeInfo = () => {
            if (WORD_TYPES.includes(fileType)) {
                return {
                    name: i18next.t("filePreview.wordDocument"),
                    apps: [i18next.t("filePreview.microsoftWord"), i18next.t("filePreview.googleDocs"), i18next.t("filePreview.libreOfficeWriter")],
                    color: "#2B579A"
                };
            }
            if (EXCEL_TYPES.includes(fileType)) {
                return {
                    name: i18next.t("filePreview.spreadsheetDocument"),
                    apps: [i18next.t("filePreview.microsoftExcel"), i18next.t("filePreview.googleSheets"), i18next.t("filePreview.libreOfficeCalc")],
                    color: "#217346"
                };
            }
            if (POWERPOINT_TYPES.includes(fileType)) {
                return {
                    name: i18next.t("filePreview.presentationDocument"),
                    apps: [i18next.t("filePreview.microsoftPowerPoint"), i18next.t("filePreview.googleSlides"), i18next.t("filePreview.libreOfficeImpress")],
                    color: "#D24726"
                };
            }
            return {
                name: i18next.t("filePreview.document"),
                apps: [i18next.t("filePreview.appropriateApplication")],
                color: "#5A5A5A"
            };
        };

        const fileInfo = getFileTypeInfo();

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    p: 4,
                    maxWidth: 600,
                    mx: "auto",
                    textAlign: "center"
                }}
            >
                <Box sx={{ mb: 3, filter: "drop-shadow(0 4px 12px rgba(255,255,255,0.2))" }}>{getFileIconByMimeType(fileType, 80)}</Box>

                <Typography variant="h5" color="white" sx={{ mb: 1, fontWeight: 600, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
                    {i18next.t("filePreview.previewNotAvailable")}
                </Typography>

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: fileInfo.color, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
                    {fileInfo.name}
                </Typography>

                <Typography variant="body1" color="rgba(255, 255, 255, 0.95)" sx={{ mb: 3, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                    {i18next.t("filePreview.downloadToView")}
                </Typography>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: "rgba(255, 255, 255, 0.9)", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>
                        {i18next.t("filePreview.compatibleApplications")}:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center" }}>
                        {fileInfo.apps.map((app, index) => (
                            <Box
                                key={index}
                                sx={{
                                    bgcolor: `${fileInfo.color}20`,
                                    backdropFilter: "blur(10px)",
                                    color: fileInfo.color,
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 2,
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    border: `2px solid ${fileInfo.color}`,
                                    textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                                }}
                            >
                                {app}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "rgba(255, 255, 255, 0.85)",
                        textShadow: "0 1px 4px rgba(0,0,0,0.8)"
                    }}
                >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{i18next.t("filePreview.downloadInstruction")}</Typography>
                </Box>
            </Box>
        );
    };

    if (!currentFile) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: "transparent",
                    boxShadow: "none",
                    width: "calc(100vw - 32px)",
                    height: "calc(100vh - 32px)",
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: "calc(100vh - 32px)",
                    minWidth: "300px",
                    minHeight: "200px",
                    margin: "16px",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "absolute"
                }
            }}
            sx={{
                "& .MuiBackdrop-root": {
                    backgroundColor: "transparent"
                },
                "& .MuiDialog-container": {
                    alignItems: "center",
                    justifyContent: "center"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: "rgba(0, 0, 0, 0.8)",
                    backdropFilter: "blur(10px)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography variant="h6" color="white" noWrap sx={{ mr: 2, maxWidth: "300px" }} title={currentFileProps?.name}>
                        {currentFileProps?.name}
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                        {getReadableFileSize(currentFileProps?.size ?? 0)}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {IMAGE_TYPES.includes(currentFileType) && (
                        <>
                            <Tooltip title={i18next.t("filePreview.zoomOut")}>
                                <IconButton onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.25))} sx={{ color: "white" }} disabled={zoom <= 0.25}>
                                    <ZoomOutIcon />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="body2" color="white" sx={{ mx: 1, minWidth: "40px", textAlign: "center" }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Tooltip title={i18next.t("filePreview.zoomIn")}>
                                <IconButton onClick={() => setZoom(prev => Math.min(prev + 0.25, 3))} sx={{ color: "white" }} disabled={zoom >= 3}>
                                    <ZoomInIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}

                    <Tooltip title={i18next.t("filePreview.download")}>
                        <IconButton onClick={handleDownload} sx={{ color: "white" }}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={i18next.t("filePreview.close")}>
                        <IconButton onClick={onClose} sx={{ color: "white" }}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    p: 0,
                    position: "relative",
                    height: "calc(100% - 64px)",
                    overflow: "hidden",
                    bgcolor: TEXT_TYPES.includes(currentFileType) ? "#ffffff" : "rgba(0, 0, 0, 0.7)",
                    backdropFilter: TEXT_TYPES.includes(currentFileType) ? "none" : "blur(10px)"
                }}
            >
                {renderFileContent()}

                {/* Navigation Buttons */}
                {hasMultipleFiles && (
                    <>
                        {currentIndex !== 0 && (
                            <Fade in timeout={300}>
                                <IconButton
                                    onClick={handlePrevious}
                                    disabled={currentIndex === 0}
                                    sx={{
                                        position: "absolute",
                                        left: 16,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        bgcolor: "rgba(0, 0, 0, 0.8)",
                                        color: "white",
                                        "&:hover": {
                                            bgcolor: "rgba(0, 0, 0, 0.8)"
                                        },
                                        "&:disabled": {
                                            bgcolor: "rgba(0, 0, 0, 0.8)",
                                            color: "rgba(255, 255, 255, 0.3)"
                                        },
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255, 255, 255, 0.2)"
                                    }}
                                >
                                    <ArrowBackIosIcon />
                                </IconButton>
                            </Fade>
                        )}

                        {currentIndex !== files.length - 1 && (
                            <Fade in timeout={300}>
                                <IconButton
                                    onClick={handleNext}
                                    disabled={currentIndex === files.length - 1}
                                    sx={{
                                        position: "absolute",
                                        right: 16,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        bgcolor: "rgba(0, 0, 0, 0.8)",
                                        color: "white",
                                        "&:hover": {
                                            bgcolor: "rgba(0, 0, 0, 0.8)"
                                        },
                                        "&:disabled": {
                                            bgcolor: "rgba(0, 0, 0, 0.8)",
                                            color: "rgba(255, 255, 255, 0.3)"
                                        },
                                        backdropFilter: "blur(10px)",
                                        border: "1px solid rgba(255, 255, 255, 0.2)"
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </Fade>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
